import type { FC } from 'react';

// DASHBOARD
export enum ActivityType {
  Immissione = 'Attrezzatura',
  Assegnazione = 'Assegnazione',
  Visualizzazione = 'Visualizzazione',
  Ritiro = 'Ritiro',
  GestioneUtenti = 'Utenti',
  Logout = 'Logout',
}

export interface Activity {
  id: ActivityType;
  label: string;
  icon: FC<any>;
  description: string;
  value: Number;
}

// TOOLS
export interface Person {
  id: string;
  name: string;
  surname: string;
}

/*export interface Warehouse {
  id: string;
  name: string;
  location: string;
  toolsCount: number;
  capacity: number;
  //tools: Tool[];
}*/

/*export interface Tool {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  personId: string | null; // Null if in a warehouse
  warehouseId: string | null; // Null if assigned to a person
  currentAssignmentLogId: string | null; // ID of the log entry if currently assigned
}*/

export interface AssignmentLogEntry {
  id: number;
  toolIds: number[];
  assigneeId: number; // Person ID
  destination: string;
  assignmentDate: string; // ISO string
  returnDate?: string | null; // ISO string, null if not returned
  confirmationPhoto?: string | null; // Base64 string or URL of the photo
  notes?: string;
}

export enum ViewMode {
  Tools = 'TOOLS',
  Personel = 'PERSONEL',
  Warehouses = 'WAREHOUSES',
  //History = 'HISTORY',
}

// USERS
export enum UserRole {
  Administrator = "Amministratore",
  User = "Fruitore",
}

/*export interface User {
  id: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}*/

export interface UserFormData {
  Name: string;
  Surname: string;
  Email:string;
  passwordInput: string;
  role: UserRole;
}

export interface AuthJson {
  name: string;
  role: string;
}

export interface WarehouseFormData {
  name: string;
  location: string;
}

export interface ClassesFormData {
  name: string;
}

export type UsersViewMode = 'users' | 'warehouses' | 'classes';

/**
 * @enum Role
 * @description Definisce i ruoli possibili per un utente nel sistema.
 */
export enum Role {
    GUEST = 'guest',
    ADMIN = 'admin'
}

// src/model/Users.ts
/**
 * @interface Users
 * @description Rappresenta un'entità utente nel sistema.
 * Corrisponde alla classe Java `com.inventoryapp.model.Users`.
 */
export interface Users {
    /**
     * L'ID univoco dell'utente.
     * Corrisponde al tipo `Long` in Java.
     */
    id?: number; // `Long` in Java tipicamente mappa a `number` in TS, `?` indica che è opzionale per le creazioni
    /**
     * Il nome dell'utente.
     */
    name: string;
    surname: string;
    /**
     * L'indirizzo email dell'utente. Deve essere unico.
     */
    email: string;
    /**
     * La password dell'utente. È marcata come opzionale qui perché non dovrebbe essere restituita dalle API GET.
     */
    password?: string; // Opzionale per le risposte API, ma richiesto per la creazione/aggiornamento
    /**
     * Il ruolo dell'utente (ADMIN o GUEST).
     */
    role: Role;
}

// src/model/Warehouse.ts
/**
 * @interface Warehouse
 * @description Rappresenta un magazzino nel sistema.
 * Corrisponde alla classe Java `com.inventoryapp.model.Warehouse`.
 */
export interface Warehouse {
    /**
     * L'ID univoco del magazzino.
     */
    id?: number; // `int` in Java mappa a `number` in TS, `?` indica che è opzionale per le creazioni
    /**
     * Il nome del magazzino.
     */
    name: string;
    /**
     * La posizione fisica del magazzino.
     */
    location?: string; // Opzionale
}

/**
 * @interface Classes
 * @description Rappresenta una classe di oggetto.
 * Corrisponde alla classe Java `com.inventoryapp.model.Classes`.
 */
export interface Classes {
    /**
     * L'ID univoco del magazzino.
     */
    id?: number; // `int` in Java mappa a `number` in TS, `?` indica che è opzionale per le creazioni
    /**
     * Il nome del magazzino.
     */
    txt: string;
}

// src/model/Tool.ts
/**
 * @interface Tool
 * @description Rappresenta un attrezzo (tool) nell'inventario.
 * Corrisponde alla classe Java `com.inventoryapp.model.Tool`.
 */
export interface Tool {
    /**
     * L'ID univoco dell'attrezzo.
     */
    id?: number; // `int` in Java mappa a `number` in TS, `?` indica che è opzionale per le creazioni
    /**
     * Il nome dell'attrezzo.
     */
    txt?: string;
    /**
     * Una descrizione dettagliata dell'attrezzo.
     */
    note?: string;
    classify: number[];
    tagid: string|null;
    updatedate: number|null;
    manutencedate: number|null;
    objimgsrc: string|null;
    /**
     * Indica se l'attrezzo è attivo o meno.
     */
    /**
     * La quantità disponibile di questo attrezzo.
     */
    //quantity: number;
    /**
     * Il magazzino a cui questo attrezzo è assegnato.
     * Viene atteso l'intero oggetto Warehouse, ma per POST/PUT, potrebbe bastare { id: number }.
     */
    warehouse: number;
    /**
     * L'utente che gestisce o a cui è assegnato questo attrezzo.
     * Viene atteso l'intero oggetto Users, ma per POST/PUT, potrebbe bastare { id: number }.
     */
    //user: number;
}

// src/model/History.ts
/**
 * @interface History
 * @description Rappresenta una voce di storico per l'assegnazione/rilascio di un attrezzo.
 * Corrisponde alla classe Java `com.inventoryapp.model.History`.
 */
export interface ToolHistory {
    /**
     * L'ID univoco della voce di storico.
     */
    id?: number; // `Long` in Java tipicamente mappa a `number` in TS, `?` indica che è opzionale per le creazioni
    /**
     * Il timestamp di quando l'attrezzo è stato assegnato.
     * Corrisponde a `LocalDateTime` in Java, che viene serializzato come stringa ISO 8601.
     * Esempio: "2024-06-22T21:00:00"
     */
    assigned: number;
    /**
     * Il timestamp di quando l'attrezzo è stato rilasciato.
     * Può essere nullo se l'attrezzo è ancora assegnato.
     * Corrisponde a `LocalDateTime` in Java, serializzato come stringa ISO 8601.
     */
    released?: number | null; // Opzionale
    /**
     * L'utente coinvolto in questa voce di storico.
     * Viene atteso l'intero oggetto Users, ma per POST/PUT, potrebbe bastare { id: number }.
     */
    userid: number;
    /**
     * L'attrezzo coinvolto in questa voce di storico.
     * Viene atteso l'intero oggetto Tool, ma per POST/PUT, potrebbe bastare { id: number }.
     */
    toolid: number;

    assignimgsrc?: string|null;
    releaseimgsrc?: string|null;
    assignnote?: string|null;
    releasenote?: string|null;
    
    destination?: string|null;

}